import {
	Box,
	Button,
	Card,
	CardContent,
	Grid,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import StaffInfoForm from './components/StaffInfoForm';
import { FormType } from '@/api/enum/formType.enum';
import { useState } from 'react';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import { useNavigate, useParams } from 'react-router-dom';
import {
	useDeleteStaffMutation,
	useGetStaffInfoQuery,
} from '@/api/staff/staffApiSlice';
import { enqueueSnackbar, closeSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import { useGetUser, useGetUserResultType } from '@/hooks/useGetUser';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import DoDisturbAltOutlinedIcon from '@mui/icons-material/DoDisturbAltOutlined';
import { showLabel } from '@/utils/helper';
import { UserRole } from '@/api/enum/userRole.enum';
import { replace } from 'lodash';

export default function StaffInfoPage() {
	const { t } = useTranslation();
	const [type, setType] = useState<FormType>(FormType.view);
	const { id } = useParams();
	const [deleteStaff] = useDeleteStaffMutation();
	const navigate = useNavigate();
	const showModal = useModal();

	const { userRoles } = useGetUser<useGetUserResultType>({
		roleObj: {
			edit: ['edit', 'staffList'],
			delete: ['delete', 'staffList'],
		},
	});

	const staffList = [
		{ name: t('common.admin'), id: UserRole.ROLE_WEB_ADMIN },
		{ name: t('common.staff'), id: UserRole.ROLE_WEB_STAFF },
	];

	const { data: staffInfo } = useGetStaffInfoQuery(id, {
		refetchOnMountOrArgChange: true,
	});

	const handleDelete = () => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={true}
				title={t('staff.deleteStaff')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>{t('staff.isDeleteStaff')}</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.isConfirm')}
				confirmationButtonProps={{ variant: 'contained' }}
				onConfirm={async () => {
					const response = await deleteStaff(id || '');
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.deleteStaffSuccess'), {
							variant: 'success',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});
						navigate('/staff-manage/delete-staff-record', { replace: true });
					}
				}}
			/>
		));
	};
	return (
		<>
			<Grid container justifyContent='center'>
				<Grid item xs={12}></Grid>
			</Grid>
			<Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
				<Box sx={{ width: '100%' }}>
					<Box display='flex' justifyContent='space-between'>
						<Typography variant='h4'>{t('staff.staffInfo')}</Typography>
						<Box>
							{userRoles.delete &&
								staffInfo?.data.role != UserRole.ROLE_WEB_ADMIN && (
									<Button
										endIcon={<DeleteOutlined />}
										onClick={handleDelete}
										sx={{ marginRight: '24px' }}
										variant='contained'
									>
										{t('button.delete')}
									</Button>
								)}
							{userRoles.edit &&
								staffInfo?.data.role != UserRole.ROLE_WEB_ADMIN && (
									<Button
										endIcon={<CreateOutlinedIcon />}
										onClick={() => setType(FormType.edit)}
										sx={{ marginRight: '24px' }}
										variant='contained'
									>
										{t('button.edit')}
									</Button>
								)}
						</Box>
					</Box>
					<Card sx={{ marginTop: 3 }}>
						{(() => {
							switch (type) {
								case FormType.view:
									return (
										<>
											<CardContent sx={{ padding: '24px' }}>
												<Grid
													container
													spacing={3}
													marginTop={0}
													marginBottom={3}
												>
													<Grid item xs={12}>
														<AppFieldView
															label={t('staff.permission')}
															value={showLabel(staffInfo?.data.role, staffList)}
														/>
													</Grid>
													<Grid item xs={12}>
														<AppFieldView
															label={t('staff.email')}
															value={staffInfo?.data.email}
														/>
													</Grid>
													<Grid item xs={12}>
														<AppFieldView
															label={t('staff.staffName')}
															value={staffInfo?.data.name}
														/>
													</Grid>
												</Grid>
											</CardContent>
										</>
									);
								case FormType.edit:
									return (
										<StaffInfoForm
											id={id || ''}
											type={type}
											setType={setType}
											staffInfo={staffInfo?.data}
										/>
									);
							}
						})()}
					</Card>
				</Box>
			</Box>
		</>
	);
}
